import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Facebook, CheckCircle2, XCircle, Loader2, Link2, Unlink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { StatCard, inputCls, F, SaveBtn } from "./CrmShared";

const MAP_TARGETS = [["", "— Skip —"], ["name", "Full Name"], ["phone", "Phone"], ["email", "Email"], ["city", "City"]];

export default function CrmFacebook({ refreshKey }) {
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [pages, setPages] = useState(null);
  const [forms, setForms] = useState(null);
  const [selPage, setSelPage] = useState("");
  const [selForms, setSelForms] = useState([]);
  const [fieldMap, setFieldMap] = useState({});
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [stage, setStage] = useState("lead");
  const [assigned, setAssigned] = useState("");
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(() => {
    api.get("/store-admin/crm/fb/status").then((r) => {
      setData(r.data);
      const c = r.data.connection;
      if (c) {
        setSelPage(c.page_id || "");
        setSelForms(c.form_ids || []);
        setFieldMap(c.field_mapping || {});
        setPipelineId(c.pipeline_id || "");
        setStage(c.default_stage || "lead");
        setAssigned(c.assigned_to || "");
      }
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/store-admin/crm/pipelines").then((r) => setPipelines(r.data.pipelines)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (sp.get("connected") === "1") toast.success("Facebook connected — now select your Page and Lead Forms");
    if (sp.get("error")) toast.error(`Facebook connection failed: ${sp.get("error")}`);
  }, [sp]);

  const connect = async () => {
    setBusy("connect");
    try {
      const r = await api.get("/store-admin/crm/fb/oauth/start");
      window.location.href = r.data.url;
    } catch (e) {
      if (e?.response?.status === 503) toast.error("Facebook integration is not configured on this platform yet (Meta App credentials pending).");
      else toast.error(errMsg(e));
    }
    setBusy("");
  };

  const loadPages = useCallback(async () => {
    try { const r = await api.get("/store-admin/crm/fb/pages"); setPages(r.data.pages); }
    catch { setPages(null); }
  }, []);
  useEffect(() => { if (data?.connection) loadPages(); }, [data, loadPages]);

  const loadForms = async (pid) => {
    setSelPage(pid); setForms(null);
    try { const r = await api.get(`/store-admin/crm/fb/pages/${pid}/forms`); setForms(r.data.forms); }
    catch (e) { toast.error(errMsg(e)); setForms([]); }
  };

  const subscribe = async (pid) => {
    setBusy("sub");
    try { await api.post(`/store-admin/crm/fb/pages/${pid}/subscribe`); toast.success("Webhook subscribed — real Meta confirmation received"); loadPages(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const saveConfig = async () => {
    setBusy("cfg");
    try {
      await api.post("/store-admin/crm/fb/config", { page_id: selPage, form_ids: selForms, field_mapping: fieldMap, pipeline_id: pipelineId, default_stage: stage, assigned_to: assigned });
      toast.success("Facebook lead sync configuration saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const test = async () => {
    setBusy("test"); setTestResult(null);
    try { const r = await api.post("/store-admin/crm/fb/test"); setTestResult(r.data); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Facebook? Existing synced leads stay in CRM.")) return;
    try { await api.post("/store-admin/crm/fb/disconnect"); toast.success("Disconnected"); setData(null); setPages(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="h-32 flex items-center justify-center"><div className="h-7 w-7 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;
  const st = data.stats;
  const conn = data.connection;

  return (
    <div className="space-y-5" data-testid="crm-facebook">
      <div>
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><Facebook className="h-5 w-5 text-blue-600" /> Facebook Lead Ads</h2>
        <p className="text-xs text-muted-foreground">Official Meta integration — leads from your Facebook Lead Ads flow into CRM automatically.</p>
      </div>

      {/* stats dashboard (works even before connect — reads CRM leads with source=Facebook) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="fb-stats">
        <StatCard label="Facebook Leads" value={st.total} testid="fb-total" />
        <StatCard label="Today" value={st.today} />
        <StatCard label="This Week" value={st.this_week} />
        <StatCard label="This Month" value={st.this_month} />
        <StatCard label="Qualified" value={st.qualified} />
        <StatCard label="Won Deals" value={st.won} tone="text-emerald-600" />
        <StatCard label="Conversion" value={`${st.conversion}%`} />
      </div>
      {Object.keys(st.by_campaign || {}).length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3">
          {[["By Page", st.by_page], ["By Form", st.by_form], ["By Campaign", st.by_campaign]].map(([t, obj]) => (
            <div key={t} className="rounded-2xl border border-border bg-background p-4">
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-2">{t}</p>
              {Object.entries(obj).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-1"><span className="truncate">{k}</span><span className="font-bold ml-2">{v}</span></div>)}
            </div>
          ))}
        </div>
      )}

      {!data.configured && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="fb-not-configured">
          <p className="font-bold text-sm text-amber-800">Facebook integration not configured yet</p>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">
            The platform admin needs to add Meta App credentials (META_APP_ID, META_APP_SECRET, webhook verify token) to enable the official
            Meta OAuth connection. Once configured, the Connect Facebook button becomes active. Your CRM form links work as ad destinations in the meantime.
          </p>
        </div>
      )}

      {data.configured && !conn && (
        <div className="rounded-2xl border border-border bg-background p-6 text-center space-y-3" data-testid="fb-connect-card">
          <div className="h-12 w-12 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto"><Facebook className="h-6 w-6 text-blue-600" /></div>
          <p className="font-bold">Step 1 — Connect Facebook</p>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">Connect your authorized Meta/Facebook account to receive Lead Ads leads in MILTROO CRM. Uses official Meta authorization — your tokens never touch the browser.</p>
          <button onClick={connect} disabled={busy === "connect"} data-testid="fb-connect"
            className="h-11 px-8 rounded-xl bg-blue-600 text-white font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {busy === "connect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Connect Facebook
          </button>
        </div>
      )}

      {conn && (
        <div className="space-y-4" data-testid="fb-wizard">
          {/* Step 2: page */}
          <div className="rounded-2xl border border-border bg-background p-5">
            <p className="font-bold text-sm mb-3">Step 2 — Select Page</p>
            {pages === null ? <p className="text-xs text-muted-foreground">Loading pages…</p>
              : pages.length === 0 ? <p className="text-xs text-muted-foreground">No pages returned by Meta for this account.</p>
              : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {pages.map((p) => (
                    <button key={p.id} onClick={() => loadForms(p.id)} data-testid={`fb-page-${p.id}`}
                      className={`rounded-xl border p-3 text-left text-sm font-bold transition-colors ${selPage === p.id ? "border-blue-500 bg-blue-50/50" : "border-border hover:bg-muted"}`}>
                      {p.name}
                      {p.subscribed && <span className="ml-2 text-[10px] text-emerald-600 font-extrabold">● Webhook active</span>}
                    </button>
                  ))}
                </div>
              )}
          </div>

          {/* Step 3: forms */}
          {selPage && (
            <div className="rounded-2xl border border-border bg-background p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="font-bold text-sm">Step 3 — Select Lead Forms</p>
                {!pages?.find((p) => p.id === selPage)?.subscribed && (
                  <button onClick={() => subscribe(selPage)} disabled={busy === "sub"} data-testid="fb-subscribe"
                    className="h-8 px-3 rounded-lg bg-blue-600 text-white text-[11px] font-extrabold disabled:opacity-60">
                    {busy === "sub" ? "…" : "Enable webhook for this page"}
                  </button>
                )}
              </div>
              {forms === null ? <p className="text-xs text-muted-foreground">Loading forms…</p>
                : forms.length === 0 ? <p className="text-xs text-muted-foreground">No lead forms on this page yet. Create one in Meta Ads Manager.</p>
                : (
                  <div className="space-y-2">
                    {forms.map((f) => (
                      <label key={f.id} className="flex items-center gap-2.5 rounded-xl border border-border p-3 cursor-pointer hover:bg-muted/50" data-testid={`fb-form-${f.id}`}>
                        <input type="checkbox" checked={selForms.includes(f.id)}
                          onChange={(e) => setSelForms(e.target.checked ? [...selForms, f.id] : selForms.filter((x) => x !== f.id))}
                          className="h-4 w-4 accent-blue-600" />
                        <span className="text-sm font-bold flex-1 truncate">{f.name}</span>
                        <span className="text-[10px] font-bold text-muted-foreground uppercase">{f.status}</span>
                      </label>
                    ))}
                  </div>
                )}
            </div>
          )}

          {/* Step 4: field mapping */}
          {selForms.length > 0 && (
            <div className="rounded-2xl border border-border bg-background p-5">
              <p className="font-bold text-sm mb-1">Step 4 — Map Facebook Fields</p>
              <p className="text-[11px] text-muted-foreground mb-3">Common fields (full_name, phone_number, email, city) are auto-mapped. Map your custom questions below (unmapped ones are kept as custom fields).</p>
              <div className="space-y-2">
                {(forms.find((f) => selForms.includes(f.id))?.questions || []).map((q) => (
                  <div key={q.key || q.label} className="flex items-center gap-2 rounded-xl border border-border p-2.5">
                    <span className="text-xs font-bold flex-1 truncate">{q.label || q.key}</span>
                    <select className="h-9 rounded-lg border border-input bg-background px-2 text-xs font-bold w-44"
                      value={fieldMap[q.key] ?? ""} onChange={(e) => setFieldMap({ ...fieldMap, [q.key]: e.target.value })}>
                      {MAP_TARGETS.map(([v, l]) => <option key={v + l} value={v}>{l}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: pipeline */}
          <div className="rounded-2xl border border-border bg-background p-5 grid sm:grid-cols-3 gap-3">
            <F label="Step 5 — Pipeline">
              <select className={inputCls} data-testid="fb-pipeline" value={pipelineId} onChange={(e) => setPipelineId(e.target.value)}>
                <option value="">Sales Pipeline (default)</option>
                {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </F>
            <F label="Default stage">
              <select className={inputCls} data-testid="fb-stage" value={stage} onChange={(e) => setStage(e.target.value)}>
                {(pipelines.find((p) => p.id === pipelineId)?.stages || pipelines[0]?.stages || [{ key: "lead", name: "Lead" }]).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
            </F>
            <F label="Assign to (optional)"><input className={inputCls} data-testid="fb-assigned" value={assigned} onChange={(e) => setAssigned(e.target.value)} placeholder="Team member" /></F>
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={saveConfig} disabled={busy === "cfg"} data-testid="fb-save-config"
              className="h-11 px-6 rounded-xl text-white font-bold text-sm disabled:opacity-60"
              style={{ backgroundImage: "linear-gradient(135deg, #059669, #0d9488)" }}>
              {busy === "cfg" ? "Saving…" : "Save Configuration"}
            </button>
            <button onClick={test} disabled={busy === "test"} data-testid="fb-test"
              className="h-11 px-6 rounded-xl border border-border font-bold text-sm flex items-center gap-2 disabled:opacity-60">
              {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Test Connection
            </button>
            <button onClick={disconnect} data-testid="fb-disconnect"
              className="h-11 px-6 rounded-xl border border-red-200 text-red-600 font-bold text-sm flex items-center gap-2"><Unlink className="h-4 w-4" /> Disconnect</button>
          </div>

          {/* Step 6: real verification results */}
          {testResult && (
            <div className="rounded-2xl border border-border bg-background p-5" data-testid="fb-test-result">
              <p className="font-bold text-sm mb-3">Step 6 — Verification {testResult.ok ? <span className="text-emerald-600">(all checks passed)</span> : <span className="text-amber-600">(needs attention)</span>}</p>
              <div className="space-y-1.5">
                {Object.entries(testResult.checks).map(([k, v]) => (
                  <p key={k} className="flex items-center gap-2 text-sm" data-testid={`fb-check-${k}`}>
                    {v ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
                    {k.replace(/_/g, " ")}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
